// Settings → Jobs: the one-click jobs each team offers under "Give a job",
// and the options (topics) to pick from. Built-in jobs can be edited or
// hidden; jobs made here can be deleted.
import { EmptyState } from '@/components/primitives/EmptyState'
import { ErrorState } from '@/components/primitives/ErrorState'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { Switch } from '@/components/ui/switch'
import {
  type JobInput,
  type QuickTask,
  type TeamAgent,
  createJob,
  deleteJob,
  fetchJobs,
  fetchTeam,
  updateJob
} from '@/lib/ai-team'
import { notifyError, queryClient } from '@/lib/query'
import { cn } from '@/lib/utils'
import { AgentAvatar } from '@/pages/ai-team/AgentAvatar'
import { CHIP, HUBS, card } from '@/pages/ai-team/hubs'
import { useQuery } from '@tanstack/react-query'
import { Briefcase, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react'
import { type FormEvent, type KeyboardEvent, useState } from 'react'
import { toast } from 'sonner'

const field =
  'w-full rounded-[36px] border border-neutral-200 bg-white px-4 py-2.5 text-sm text-neutral-900 outline-none transition-colors placeholder:text-neutral-400 focus:border-emerald-500 dark:border-neutral-800 dark:bg-neutral-900 dark:text-white dark:placeholder:text-neutral-500'
const goBtn =
  'inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-bold text-neutral-950 transition-all hover:bg-emerald-400 active:scale-95 disabled:opacity-50'
const iconBtn =
  'inline-flex size-9 items-center justify-center rounded-full text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900 disabled:opacity-50 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-white'

async function refreshJobs() {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['ai-jobs'] }),
    queryClient.invalidateQueries({ queryKey: ['ai-team'] })
  ])
}

export function JobsPage() {
  const jobs = useQuery({ queryKey: ['ai-jobs'], queryFn: fetchJobs })
  const team = useQuery({ queryKey: ['ai-team'], queryFn: fetchTeam })
  const [editing, setEditing] = useState<QuickTask | 'new' | null>(null)

  const agents = team.data?.agents ?? []
  const byKey = new Map(agents.map((a) => [a.key, a]))

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Jobs</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            What each team can do from "Give a job", and the topics to pick from.
          </p>
        </div>
        <button type="button" className={goBtn} onClick={() => setEditing('new')} disabled={!agents.length}>
          <Plus className="size-4" /> Add a job
        </button>
      </div>

      {(jobs.isLoading || team.isLoading) && (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-40 animate-pulse rounded-[36px] bg-neutral-200 motion-reduce:animate-none dark:bg-neutral-800" />
          ))}
        </div>
      )}
      {jobs.error && <ErrorState error={jobs.error} onRetry={() => jobs.refetch()} />}
      {team.error && !jobs.error && <ErrorState error={team.error} onRetry={() => team.refetch()} />}
      {jobs.data && jobs.data.jobs.length === 0 && (
        <EmptyState
          icon={Briefcase}
          title="No jobs yet"
          description="Add one so a team can be given it in one click."
          action={
            <button type="button" className={goBtn} onClick={() => setEditing('new')} disabled={!agents.length}>
              <Plus className="size-4" /> Add a job
            </button>
          }
        />
      )}

      {jobs.data &&
        agents.length > 0 &&
        HUBS.map((hub) => {
          const keys = new Set(agents.filter((a) => hub.layers.includes(a.layer)).map((a) => a.key))
          const list = jobs.data.jobs.filter((j) => keys.has(j.agent))
          if (list.length === 0) return null
          return (
            <section key={hub.id} className={cn(card, 'space-y-3')}>
              <h2 className="flex items-center gap-3 text-lg font-bold text-neutral-900 dark:text-white">
                <span className={cn('flex size-9 items-center justify-center rounded-full', CHIP[hub.tone])}>
                  <hub.icon className="size-4" />
                </span>
                {hub.title}
                <span className="text-sm font-semibold text-neutral-500 dark:text-neutral-400">{list.length}</span>
              </h2>
              <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
                {list.map((j) => (
                  <JobRow key={j.id} job={j} agent={byKey.get(j.agent)} onEdit={() => setEditing(j)} />
                ))}
              </ul>
            </section>
          )
        })}

      <JobDialog
        job={editing}
        agents={agents}
        onClose={() => setEditing(null)}
      />
    </div>
  )
}

function JobRow({ job, agent, onEdit }: { job: QuickTask; agent?: TeamAgent; onEdit: () => void }) {
  const [busy, setBusy] = useState(false)

  async function toggle(enabled: boolean) {
    setBusy(true)
    try {
      await updateJob(job.id, { enabled })
      await refreshJobs()
    } catch (err) {
      notifyError('Could not change the job', err)
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    if (!window.confirm(`Delete "${job.label}"?`)) return
    setBusy(true)
    try {
      await deleteJob(job.id)
      toast.success('Job deleted')
      await refreshJobs()
    } catch (err) {
      notifyError('Could not delete the job', err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <li className={cn('flex flex-wrap items-center gap-3 py-3', !job.enabled && 'opacity-60')}>
      <AgentAvatar agentKey={job.agent} name={agent?.name} size="sm" />
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-neutral-900 dark:text-white">
          {job.label}
          {job.builtin && (
            <span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold', CHIP.neutral)}>Built-in</span>
          )}
          {!job.enabled && (
            <span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold', CHIP.amber)}>Hidden</span>
          )}
        </p>
        <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">
          {agent?.name ?? job.agent}
          {job.choice ? ` · ${job.choice.label}: ${job.choice.options.join(', ')}` : ''}
        </p>
      </div>
      <Switch
        checked={job.enabled}
        disabled={busy}
        onCheckedChange={toggle}
        aria-label={job.enabled ? `Hide ${job.label}` : `Show ${job.label}`}
      />
      <button type="button" className={iconBtn} onClick={onEdit} aria-label={`Edit ${job.label}`}>
        <Pencil className="size-4" />
      </button>
      {!job.builtin && (
        <button type="button" className={iconBtn} onClick={remove} disabled={busy} aria-label={`Delete ${job.label}`}>
          {busy ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
        </button>
      )}
    </li>
  )
}

function JobDialog({
  job,
  agents,
  onClose
}: {
  job: QuickTask | 'new' | null
  agents: TeamAgent[]
  onClose: () => void
}) {
  return (
    <Dialog open={job !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-[36px] p-6">
        {job !== null && (
          // Keyed so the form starts fresh for each job.
          <JobForm key={job === 'new' ? 'new' : job.id} job={job === 'new' ? null : job} agents={agents} onDone={onClose} />
        )}
      </DialogContent>
    </Dialog>
  )
}

function JobForm({ job, agents, onDone }: { job: QuickTask | null; agents: TeamAgent[]; onDone: () => void }) {
  const [agent, setAgent] = useState(job?.agent ?? agents[0]?.key ?? '')
  const [label, setLabel] = useState(job?.label ?? '')
  const [task, setTask] = useState(job?.task ?? '')
  const [choiceLabel, setChoiceLabel] = useState(job?.choice?.label ?? '')
  const [options, setOptions] = useState<string[]>(job?.choice?.options ?? [])
  const [draft, setDraft] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState('')

  function addOption() {
    const v = draft.trim()
    if (v && !options.includes(v)) setOptions([...options, v])
    setDraft('')
  }
  function onOptionKey(e: KeyboardEvent<HTMLInputElement>) {
    // Enter that confirms an IME composition isn't "add this topic".
    if (e.nativeEvent.isComposing || e.keyCode === 229) return
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addOption()
    }
  }

  async function save(e: FormEvent) {
    e.preventDefault()
    setPending(true)
    setError('')
    const all = draft.trim() && !options.includes(draft.trim()) ? [...options, draft.trim()] : options
    const body: JobInput = { label, task, choice_label: choiceLabel, options: all }
    if (!job || !job.builtin) body.agent = agent
    try {
      if (job) await updateJob(job.id, body)
      else await createJob(body)
      toast.success(job ? 'Job saved' : 'Job added')
      await refreshJobs()
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the job')
    } finally {
      setPending(false)
    }
  }

  return (
    <form onSubmit={save} className="space-y-4">
      <div>
        <DialogTitle className="text-lg font-bold text-neutral-900 dark:text-white">
          {job ? 'Edit job' : 'Add a job'}
        </DialogTitle>
        <DialogDescription className="text-sm text-neutral-500 dark:text-neutral-400">
          It shows under "Give a job" on that agent's team card.
        </DialogDescription>
      </div>

      <label className="block space-y-1">
        <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">Agent</span>
        <select
          value={agent}
          onChange={(e) => setAgent(e.target.value)}
          disabled={!!job?.builtin}
          className={cn(field, 'disabled:opacity-60')}
        >
          {HUBS.map((h) => (
            <optgroup key={h.id} label={h.title}>
              {agents
                .filter((a) => h.layers.includes(a.layer))
                .map((a) => (
                  <option key={a.key} value={a.key}>
                    {a.name}
                  </option>
                ))}
            </optgroup>
          ))}
        </select>
      </label>

      <label className="block space-y-1">
        <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">Job name</span>
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Draft a social post" className={field} required maxLength={80} />
      </label>

      <label className="block space-y-1">
        <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">What the agent should do</span>
        <textarea
          value={task}
          onChange={(e) => setTask(e.target.value)}
          rows={3}
          required
          placeholder="e.g. Draft one social post about {choice}."
          className={cn(field, 'resize-none rounded-[24px]')}
        />
        <span className="block text-xs text-neutral-500 dark:text-neutral-400">
          Write <code className="rounded-full bg-neutral-100 px-1.5 dark:bg-neutral-800">{'{choice}'}</code> where the picked topic goes.
        </span>
      </label>

      <div className="space-y-2 rounded-[24px] border border-neutral-200 p-4 dark:border-neutral-800">
        <label className="block space-y-1">
          <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">Topic name (optional)</span>
          <input value={choiceLabel} onChange={(e) => setChoiceLabel(e.target.value)} placeholder="e.g. Topic, Period, How many" className={field} maxLength={40} />
        </label>
        <div className="space-y-1">
          <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">Topics to pick from</span>
          {options.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {options.map((o) => (
                <span key={o} className={cn('inline-flex items-center gap-1 rounded-full py-1 pl-3 pr-1 text-xs font-semibold', CHIP.emerald)}>
                  {o}
                  <button
                    type="button"
                    onClick={() => setOptions(options.filter((x) => x !== o))}
                    className="inline-flex size-5 items-center justify-center rounded-full hover:bg-emerald-500/20"
                    aria-label={`Remove ${o}`}
                  >
                    <X className="size-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onOptionKey}
              placeholder="Type a topic, press Enter"
              className={field}
              aria-label="New topic"
            />
            <button type="button" onClick={addOption} className={cn(iconBtn, 'shrink-0 border border-neutral-200 dark:border-neutral-800')} aria-label="Add topic">
              <Plus className="size-4" />
            </button>
          </div>
        </div>
      </div>

      {error && (
        <p role="alert" className="rounded-[24px] bg-red-500/10 px-4 py-2 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onDone}
          className="rounded-full border border-neutral-200 px-5 py-2.5 text-sm font-semibold text-neutral-900 hover:bg-neutral-50 dark:border-neutral-800 dark:text-white dark:hover:bg-neutral-900"
        >
          Cancel
        </button>
        <button type="submit" disabled={pending} className={goBtn}>
          {pending && <Loader2 className="size-4 animate-spin" />}
          {job ? 'Save' : 'Add job'}
        </button>
      </div>
    </form>
  )
}
